import { useEffect, useState } from "react";
import api from "../../api";
import { words } from "./format";

/**
 * Names for the workspace, person and capability ids the agent returns,
 * learned from every view the page loads: `names` (hp-admin's lookup in HP's
 * records), `workspaces` (the agent's own slug for each workspace, so a label
 * survives when that lookup is unavailable) and `capabilityInfo`.
 */
export const NAMES = { accounts: new Map(), users: new Map(), capabilities: new Map() };

export function rememberNames(body) {
  if (!body || typeof body !== "object") return;
  for (const [id, slug] of Object.entries(body.workspaces ?? {})) NAMES.accounts.set(id, { slug, ...(NAMES.accounts.get(id) ?? {}) });
  for (const [id, v] of Object.entries(body.names?.accounts ?? {})) NAMES.accounts.set(id, { ...(NAMES.accounts.get(id) ?? {}), ...v });
  for (const [id, v] of Object.entries(body.names?.users ?? {})) NAMES.users.set(id, v);
  for (const [id, v] of Object.entries(body.capabilityInfo ?? {})) NAMES.capabilities.set(id, v);
}

const shortId = (id) => (id && id.length > 10 ? `${id.slice(0, 6)}…` : id);

export function workspaceLabel(id) {
  if (!id) return "—";
  const n = NAMES.accounts.get(id);
  return n?.name || n?.slug || `Workspace ${shortId(id)}`;
}

/** A person as the page shows them: their name, else their email, else a short id. */
export function personLabel(id) {
  if (!id) return "—";
  const n = NAMES.users.get(id);
  return n?.name || n?.email || `Person ${shortId(id)}`;
}

export const personEmail = (id) => NAMES.users.get(id)?.email ?? null;

export const capabilityLabel = (id) => NAMES.capabilities.get(id)?.label ?? words(id);

/**
 * One analytics view, fetched through hp-admin's proxy. The previous answer
 * stays on screen while a new one loads (`refreshing`), so changing a filter
 * never blanks the page; a superseded request is dropped.
 */
export function useView(view, params, extra, { skip = false } = {}) {
  const [state, setState] = useState({ data: null, loading: !skip, refreshing: false, error: null });
  const key = JSON.stringify({ view, params, extra, skip });
  useEffect(() => {
    if (skip) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: !s.data, refreshing: Boolean(s.data), error: null }));
    const clean = Object.fromEntries(Object.entries({ ...params, ...(extra ?? {}) }).filter(([k, v]) => v != null && v !== "" && k !== "nonce"));
    api
      .get(`/api/agent-analytics/${view}`, { params: clean })
      .then((res) => {
        rememberNames(res.data);
        if (live) setState({ data: res.data, loading: false, refreshing: false, error: null });
      })
      .catch((err) => live && setState((s) => ({ ...s, loading: false, refreshing: false, error: err?.response?.data?.error || err.message })));
    return () => {
      live = false;
    };
  }, [key]);
  return state;
}

export async function fetchSession(sessionId, params) {
  const res = await api.get(`/api/agent-analytics/sessions/${encodeURIComponent(sessionId)}`, { params });
  rememberNames(res.data);
  return res.data;
}
